import React, { useEffect, useState } from "react";
import { Box, Flex, SegmentGroup, Stack, Text } from "@chakra-ui/react";
import { LuX } from "react-icons/lu";
import { Button } from "../../components/ui/button";
import { env, useGlobalState } from "../../helpers/GlobalStateProvider";
import { TIER_CREDITS } from "../../helpers/tierConstants";
import { getApiUrl } from "../../config";
import { getToken } from "../../helpers/tokenManager";
import { safeRedirect } from "../../helpers/validateRedirectUrl";
import { redirectToPortal } from "../../helpers/stripePortal";
import { defaultStakingTiers, planTiers } from "../APIPlans/planData";
import type { PlanTier } from "../APIPlans/types";

function getCallsPerMinute(plan: PlanTier): number {
  return defaultStakingTiers.find((s) => s.tier === plan.monthlyTier)?.callsPerMinute ?? 0;
}

type Cycle = "month" | "year";

interface ChangePlanPickerProps {
  onClose: () => void;
}

function formatCredits(credits: number): string {
  if (credits >= 1_000_000) return `${(credits / 1_000_000).toFixed(credits % 1_000_000 === 0 ? 0 : 1)}M`;
  if (credits >= 1_000) return `${(credits / 1_000).toFixed(0)}K`;
  return `${credits}`;
}

/**
 * Returns a friendly diff string comparing a target plan to the user's current
 * plan, in plain English. e.g. "−3.5M credits/mo · −600 calls/min · Save $320/mo"
 */
function buildDiff(
  current: { plan: PlanTier | null; cycle: Cycle | null },
  target: { plan: PlanTier; cycle: Cycle },
): string {
  const targetTier = target.cycle === "year" ? target.plan.annualTier : target.plan.monthlyTier;
  const toCredits = TIER_CREDITS[targetTier] ?? 0;

  if (!current.plan || !current.cycle) {
    return `${formatCredits(toCredits)} credits/mo · ${getCallsPerMinute(target.plan)}/min`;
  }
  const currentTier = current.cycle === "year" ? current.plan.annualTier : current.plan.monthlyTier;
  const fromCredits = TIER_CREDITS[currentTier] ?? 0;
  const creditsDiff = toCredits - fromCredits;
  const callsDiff = getCallsPerMinute(target.plan) - getCallsPerMinute(current.plan);
  const fromMonthly =
    current.cycle === "year" ? current.plan.annualPrice : current.plan.monthlyPrice;
  const toMonthly =
    target.cycle === "year" ? target.plan.annualPrice : target.plan.monthlyPrice;
  const priceDiffMonthly = toMonthly - fromMonthly;

  const parts: string[] = [];
  if (creditsDiff !== 0) {
    parts.push(`${creditsDiff > 0 ? "+" : "−"}${formatCredits(Math.abs(creditsDiff))} credits/mo`);
  }
  if (callsDiff !== 0) {
    parts.push(`${callsDiff > 0 ? "+" : "−"}${Math.abs(callsDiff)} calls/min`);
  }
  if (priceDiffMonthly !== 0) {
    if (priceDiffMonthly < 0) {
      parts.push(`Save $${Math.abs(priceDiffMonthly)}/mo`);
    } else {
      parts.push(`+$${priceDiffMonthly}/mo`);
    }
  }
  if (current.cycle !== target.cycle) {
    if (target.cycle === "year") {
      parts.push("billed annually (save 20%)");
    } else {
      parts.push("billed monthly");
    }
  }
  return parts.join(" · ") || "Same plan";
}

const ChangePlanPicker: React.FC<ChangePlanPickerProps> = ({ onClose }) => {
  const { info } = useGlobalState();

  const currentBillingInterval = ((info as any)?.billingInterval as Cycle | null) || null;
  const [selectedCycle, setSelectedCycle] = useState<Cycle>(currentBillingInterval ?? "month");

  // Resolve user's current plan (mirrors dashboard tier resolution)
  const effectiveTier =
    info?.stripeTier && info.stripeTier > 0
      ? info.stripeTier
      : info?.stakedTier && info.stakedTier > 0
        ? info.stakedTier
        : (info?.tier ?? 0);
  const currentPlan = planTiers.find(
    (p) => p.monthlyTier === effectiveTier || p.annualTier === effectiveTier,
  ) ?? null;

  const [tierLinks, setTierLinks] = useState<{ [key: string]: string }>({});
  const [customerId, setCustomerId] = useState<string | null>(null);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const response = await fetch(`${getApiUrl(env)}/webhooks/buy`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        setCustomerId(data.customerId ?? null);
        setTierLinks(data.tiers || {});
      } catch {
        /* fall through */
      }
    };
    fetchLinks();
  }, []);

  const handleSwitchTo = (targetTier: number) => {
    // For users with an active customer ID, route everything through Stripe portal
    // (handles both upgrades and downgrades, prorations, and cycle changes).
    if (customerId) {
      // Pre-authenticated portal session; falls back to the static login link.
      void redirectToPortal();
      return;
    }
    // For new subscribers (no customer ID yet), use the per-tier checkout link.
    const link = tierLinks[String(targetTier)];
    if (link) {
      safeRedirect(link);
    }
  };

  return (
    <Box mt={4} bg="#fafafa" border="1px solid" borderColor="#e4e4e7" borderRadius="12px" p={4}>
      {/* Header */}
      <Flex alignItems="center" justifyContent="space-between" mb={4}>
        <Text fontSize="md" fontWeight="bold" color="#000">
          Change plan
        </Text>
        <Box
          as="button"
          aria-label="Close"
          onClick={onClose}
          color="#71717a"
          _hover={{ color: "#000" }}
        >
          <LuX size={18} />
        </Box>
      </Flex>

      {/* Cycle toggle */}
      <SegmentGroup.Root
        value={selectedCycle}
        onValueChange={(e) => setSelectedCycle(e.value as Cycle)}
        width="fit-content"
        mb={4}
      >
        <SegmentGroup.Indicator />
        <SegmentGroup.Item value="month">
          <SegmentGroup.ItemText>Monthly</SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
        <SegmentGroup.Item value="year">
          <SegmentGroup.ItemText>Annual (save 20%)</SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
      </SegmentGroup.Root>

      {/* Plan list */}
      <Stack gap={2}>
        {planTiers.map((plan) => {
          const isCurrent =
            currentPlan?.title === plan.title && currentBillingInterval === selectedCycle;
          const isHigherTier =
            !!currentPlan && plan.monthlyTier > currentPlan.monthlyTier;
          const isLowerTier =
            !!currentPlan && plan.monthlyTier < currentPlan.monthlyTier;
          const buttonLabel = isCurrent
            ? "— Current —"
            : isHigherTier
              ? "Upgrade"
              : isLowerTier
                ? "Downgrade"
                : "Switch";

          const monthlyEquivalent =
            selectedCycle === "year" ? plan.annualPrice : plan.monthlyPrice;
          const diff = buildDiff(
            { plan: currentPlan, cycle: currentBillingInterval },
            { plan, cycle: selectedCycle },
          );

          return (
            <Flex
              key={plan.title}
              bg={isCurrent ? "#f3e8ff" : "#fff"}
              border="1px solid"
              borderColor={isCurrent ? "#d8b4fe" : "#e4e4e7"}
              borderRadius="8px"
              p={3}
              alignItems="center"
              justifyContent="space-between"
              gap={3}
            >
              <Stack gap="2px" flex={1}>
                <Text fontSize="sm" fontWeight="bold" color={isCurrent ? "#7c3aed" : "#000"}>
                  {plan.title} · ${monthlyEquivalent}/mo
                  {selectedCycle === "year" && (
                    <Text as="span" ml={2} fontSize="xs" color="#1d4ed8" fontWeight="bold">
                      ANNUAL
                    </Text>
                  )}
                </Text>
                {isCurrent ? (
                  <Text fontSize="xs" color="#7c3aed" fontWeight="semibold">
                    YOUR CURRENT PLAN
                  </Text>
                ) : (
                  <Text fontSize="xs" color="#71717a">
                    {diff}
                  </Text>
                )}
              </Stack>
              {isCurrent ? (
                <Text fontSize="xs" color="#7c3aed" fontWeight="semibold">
                  Current
                </Text>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const targetTier = selectedCycle === "year" ? plan.annualTier : plan.monthlyTier;
                    handleSwitchTo(targetTier);
                  }}
                >
                  {buttonLabel}
                </Button>
              )}
            </Flex>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ChangePlanPicker;
